import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { ExternalLink, Radio, Tv, BookOpen, Globe, Newspaper } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEDIA_TYPES = {
    tv: {
        label: "Televisão",
        icon: Tv,
        badge: "bg-rose-100 text-rose-700 border border-rose-200",
        border: "border-l-rose-500",
        dot: "bg-rose-500",
    },
    radio: {
        label: "Rádio",
        icon: Radio,
        badge: "bg-violet-100 text-violet-700 border border-violet-200",
        border: "border-l-violet-500",
        dot: "bg-violet-500",
    },
    print: {
        label: "Imprensa",
        icon: Newspaper,
        badge: "bg-[var(--acd-navy-800)]/10 text-[var(--acd-navy-800)] border border-[var(--acd-navy-800)]/20",
        border: "border-l-[var(--acd-navy-700)]",
        dot: "bg-[var(--acd-navy-700)]",
    },
    online: {
        label: "Online",
        icon: Globe,
        badge: "bg-emerald-100 text-emerald-700 border border-emerald-200",
        border: "border-l-emerald-500",
        dot: "bg-emerald-500",
    },
};

const FILTERS = [
    { key: "all", label: "Todos" },
    { key: "tv", label: "Televisão" },
    { key: "radio", label: "Rádio" },
    { key: "print", label: "Imprensa" },
    { key: "online", label: "Online" },
];

const FeaturedCard = ({ item }) => {
    const cfg = MEDIA_TYPES[item.media_type] || MEDIA_TYPES.online;
    const Icon = cfg.icon;
    return (
        <div
            className="relative overflow-hidden rounded-2xl bg-[var(--acd-navy-950)] text-white mb-14"
            data-testid="press-featured-card"
        >
            {/* Decorative background pattern */}
            <div className="absolute inset-0 opacity-5"
                style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)", backgroundSize: "20px 20px" }} />
            <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[var(--acd-gold)]/5 -translate-y-1/2 translate-x-1/4 blur-3xl" />

            <div className="relative z-10 flex flex-col lg:flex-row">
                {/* Text */}
                <div className="flex-1 p-8 md:p-12 lg:p-16">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="acd-eyebrow text-[var(--acd-gold)]">Destaque</span>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${cfg.badge}`}>
                            <Icon size={12} /> {cfg.label}
                        </span>
                    </div>

                    {/* Giant decorative quote mark — only when no image */}
                    {!item.image_url && (
                        <div className="absolute top-8 right-8 md:right-16 text-white/5 font-serif leading-none select-none"
                            style={{ fontSize: "14rem", lineHeight: 1 }}>
                            &ldquo;
                        </div>
                    )}

                    <div className="max-w-2xl">
                        <div className="text-white/50 text-sm font-semibold uppercase tracking-widest mb-3">
                            {item.media_outlet} &nbsp;·&nbsp; {item.date_display}
                        </div>
                        <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-6 text-white">
                            {item.title}
                        </h2>
                        {item.excerpt && (
                            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-2xl">
                                {item.excerpt}
                            </p>
                        )}
                        {item.url && (
                            <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="acd-btn-gradient inline-flex items-center gap-2"
                                data-testid="press-featured-link"
                            >
                                Ver publicação original <ExternalLink size={14} />
                            </a>
                        )}
                    </div>
                </div>

                {/* Image panel */}
                {item.image_url && (
                    <div className="lg:w-72 xl:w-80 shrink-0 overflow-hidden">
                        <img
                            src={item.image_url}
                            alt={item.title}
                            className="w-full h-full object-cover object-top lg:rounded-r-2xl"
                            style={{ minHeight: "320px", maxHeight: "480px" }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const PressCard = ({ item }) => {
    const cfg = MEDIA_TYPES[item.media_type] || MEDIA_TYPES.online;
    const Icon = cfg.icon;
    return (
        <article
            className={`bg-white rounded-xl border border-[var(--acd-border)] border-l-4 ${cfg.border} overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col`}
            data-testid={`press-card-${item.id?.substring(0, 8)}`}
        >
            {/* Thumbnail */}
            {item.image_url && (
                <div className="h-44 overflow-hidden bg-gray-100">
                    <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                </div>
            )}

            <div className="p-6 flex flex-col gap-3 flex-1">
                <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${cfg.badge}`}>
                        <Icon size={11} /> {cfg.label}
                    </span>
                    <span className="text-xs text-[color:var(--acd-muted)]">{item.date_display}</span>
                </div>

                <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-[color:var(--acd-muted)] mb-1">
                        {item.media_outlet}
                    </div>
                    <h3 className="text-base font-bold text-[var(--acd-navy-900)] leading-snug">
                        {item.title}
                    </h3>
                </div>

                {item.excerpt && (
                    <p className="text-sm text-[color:var(--acd-muted)] leading-relaxed italic line-clamp-3 flex-1">
                        "{item.excerpt}"
                    </p>
                )}

                {item.url && (
                    <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--acd-blue-600)] hover:text-[var(--acd-navy-900)] transition-colors mt-auto"
                        data-testid={`press-card-link-${item.id?.substring(0, 8)}`}
                    >
                        Ver publicação <ExternalLink size={13} />
                    </a>
                )}
            </div>
        </article>
    );
};

const OUTLET_STRIP = ["TSF", "RTP", "Expresso", "Público", "SIC", "TVI", "Jornal de Negócios", "Observador"];

export default function Press() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState("all");

    useEffect(() => {
        window.scrollTo(0, 0);
        axios
            .get(`${API}/press`)
            .then((r) => setItems(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const featured = useMemo(() => items.find((i) => i.featured), [items]);
    const filtered = useMemo(() => {
        const list = [...items]; // include featured item in the grid too
        if (activeFilter === "all") return list;
        return list.filter((i) => i.media_type === activeFilter);
    }, [items, activeFilter]);

    const outletNames = useMemo(() => {
        const names = [...new Set(items.map((i) => i.media_outlet))];
        return names.length > 0 ? names : OUTLET_STRIP;
    }, [items]);

    return (
        <div data-testid="page-press">
            <SEO
                title="Press | ACD Advogados na Imprensa"
                description="ACD Advogados na imprensa, rádio e televisão. Entrevistas, artigos de opinião e presença mediática dos nossos especialistas em direito."
                canonicalPath="/press"
            />

            {/* ── Hero ──────────────────────────────────────────────── */}
            <section className="relative py-24 md:py-32 px-6 bg-[var(--acd-navy-950)] text-white overflow-hidden">
                {/* Grain texture overlay */}
                <div className="absolute inset-0 opacity-[0.03]"
                    style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")", backgroundSize: "200px" }} />

                {/* Gold accent glow */}
                <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[var(--acd-gold)]/10 blur-3xl" />
                <div className="absolute top-10 right-0 w-64 h-64 rounded-full bg-[var(--acd-blue-600)]/15 blur-3xl" />

                <div className="relative z-10 max-w-7xl mx-auto">
                    <div className="max-w-3xl">
                        <span className="acd-eyebrow">Imprensa &amp; Média</span>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mt-3 mb-6">
                            ACD Advogados<br className="hidden md:block" />
                            <span className="text-[var(--acd-gold)]"> na Imprensa</span>
                        </h1>
                        <p className="text-white/65 text-lg md:text-xl leading-relaxed max-w-xl">
                            Os nossos especialistas são fonte de referência para os principais meios de comunicação social portugueses.
                        </p>
                    </div>
                </div>
            </section>

            {/* ── Outlets strip ─────────────────────────────────────── */}
            {outletNames.length > 0 && (
                <div className="bg-white border-b border-[var(--acd-border)] py-5 px-6 overflow-hidden">
                    <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
                        <span className="text-xs uppercase tracking-widest font-bold text-[color:var(--acd-muted)] shrink-0 mr-2">
                            Destaque em:
                        </span>
                        {outletNames.map((name) => (
                            <span key={name} className="text-sm font-semibold text-[var(--acd-navy-800)]/50 px-3 py-1">
                                {name}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Main content ──────────────────────────────────────── */}
            <section className="py-16 md:py-20 px-6 bg-[var(--acd-bg-soft)] min-h-[50vh]">
                <div className="max-w-7xl mx-auto">
                    {loading ? (
                        <div className="text-center py-20 text-[color:var(--acd-muted)]">
                            A carregar...
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-20" data-testid="press-empty">
                            <BookOpen size={48} className="mx-auto mb-4 text-[color:var(--acd-muted)] opacity-30" />
                            <p className="text-[color:var(--acd-muted)] text-lg font-medium">
                                Em breve aqui as nossas aparições na imprensa.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Featured */}
                            {featured && <FeaturedCard item={featured} />}

                            {/* Filter tabs */}
                            <div className="flex items-center gap-2 flex-wrap mb-10" data-testid="press-filters">
                                {FILTERS.map((f) => (
                                    <button
                                        key={f.key}
                                        onClick={() => setActiveFilter(f.key)}
                                        className={`px-4 py-2 rounded-full text-sm font-bold transition-all duration-200 ${
                                            activeFilter === f.key
                                                ? "bg-[var(--acd-navy-900)] text-white shadow-md"
                                                : "bg-white text-[var(--acd-navy-900)] border border-[var(--acd-border)] hover:border-[var(--acd-navy-700)]"
                                        }`}
                                        data-testid={`press-filter-${f.key}`}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                                <span className="ml-2 text-xs text-[color:var(--acd-muted)]">
                                    {filtered.length} {filtered.length === 1 ? "item" : "itens"}
                                </span>
                            </div>

                            {/* Grid */}
                            {filtered.length === 0 ? (
                                <div className="text-center py-16 text-[color:var(--acd-muted)]" data-testid="press-filter-empty">
                                    Sem resultados para este filtro.
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filtered.map((item) => (
                                        <PressCard key={item.id} item={item} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>

            {/* ── Media Kit CTA ─────────────────────────────────────── */}
            <section className="py-16 px-6 bg-[var(--acd-navy-900)] text-white">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
                    <div>
                        <span className="acd-eyebrow">Imprensa</span>
                        <h2 className="text-2xl md:text-3xl font-bold text-white mt-2">
                            Pedidos de declarações ou entrevistas?
                        </h2>
                        <p className="text-white/60 mt-2 max-w-lg">
                            Os nossos advogados estão disponíveis para comentar questões jurídicas de atualidade. Contacte-nos através dos nossos canais habituais.
                        </p>
                    </div>
                    <a
                        href="/#contactos"
                        className="acd-btn-gradient shrink-0"
                        data-testid="press-contact-cta"
                    >
                        Contactar assessoria
                    </a>
                </div>
            </section>
        </div>
    );
}
